import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatTeardropDots, Eye, EyeSlash, DownloadSimple, Sparkle, ArrowsClockwise, Paperclip, Trash, PencilSimple, Question, PaperPlaneTilt, CheckCircle, WarningCircle, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const FIXED_KEYS = ["q1", "q2", "q3", "q4", "q5"]; // eslint-disable-line no-unused-vars

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function FeedbackAdmin() {
  const [info, setInfo] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [emailCounts, setEmailCounts] = useState({ sent: 0, failed: 0, unknown: 0, no_email: 0 });
  const [questions, setQuestions] = useState([]);
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [toggleDialog, setToggleDialog] = useState(false);
  const [summaryCache, setSummaryCache] = useState(null);
  const [summarising, setSummarising] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resendingOne, setResendingOne] = useState(null); // submission id mid-flight
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkTarget, setBulkTarget] = useState("unsent"); // "unsent" | "failed" | "all"
  const [bulkLimit, setBulkLimit] = useState(90);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [infoRes, subs, summary, cfg] = await Promise.all([
        api.get("/info"),
        api.get("/admin/feedback/submissions"),
        api.get("/admin/feedback/summary").catch(() => ({ data: { summary: null } })),
        api.get("/feedback/config"),
      ]);
      setInfo(infoRes.data || {});
      setSubmissions(subs.data.submissions || []);
      setEmailCounts(subs.data.email_counts || { sent: 0, failed: 0, unknown: 0, no_email: 0 });
      setSummaryCache(summary.data && summary.data.summary ? summary.data : null);
      setQuestions(cfg.data.questions || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const togglePublished = async () => {
    try {
      await api.post("/admin/feedback/publish", { published: !info.feedback_published });
      toast.success(!info.feedback_published
        ? "Feedback is LIVE - the bottom-nav Playlist tab is now Feedback"
        : "Feedback hidden from delegates - Playlist is back in the nav");
      setToggleDialog(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await api.patch("/info", {
        feedback_email_subject: info.feedback_email_subject || "",
        feedback_email_body: info.feedback_email_body || "",
      });
      setInfo((cur) => ({ ...cur, ...data }));
      toast.success("Email content saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingEmail(false); }
  };

  const sendPreviewEmail = async () => {
    setSendingPreview(true);
    try {
      const { data } = await api.post("/admin/feedback/preview-email");
      toast.success(`Preview sent to ${data.sent_to}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSendingPreview(false); }
  };

  const saveQuestions = async () => {
    setSavingQuestions(true);
    try {
      const { data } = await api.post("/admin/feedback/questions", { questions });
      setQuestions(data.questions);
      toast.success("Questions updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingQuestions(false); }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";  // allow re-picking same file
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File too large (20 MB max)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/feedback/attachment", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInfo((cur) => ({ ...cur, feedback_attachment: data.attachment }));
      toast.success(`Attached ${data.attachment.name}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const deleteAttachment = async () => {
    try {
      await api.delete("/admin/feedback/attachment");
      setInfo((cur) => {
        const { feedback_attachment: _x, ...rest } = cur || {};
        return rest;
      });
      toast.success("Attachment removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/feedback/submissions.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "dftb26_feedback.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const generateSummary = async () => {
    setSummarising(true);
    try {
      const { data } = await api.post("/admin/feedback/summary/generate");
      setSummaryCache(data);
      toast.success(`AI summary ready - ${data.submission_count} responses analysed`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSummarising(false); }
  };

  const resendOne = async (sub) => {
    if (!sub?.user_email) {
      toast.error("This submission has no email on file");
      return;
    }
    setResendingOne(sub.id);
    try {
      const { data } = await api.post(`/admin/feedback/submissions/${sub.id}/resend`);
      toast.success(`Sent to ${data.sent_to}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setResendingOne(null); }
  };

  const runBulkResend = async () => {
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/admin/feedback/resend-bulk", {
        target: bulkTarget,
        limit: Math.max(1, Math.min(500, Number(bulkLimit) || 90)),
      });
      setBulkResult(data);
      if (data.background) {
        toast.success(`Sending ${data.queued} emails in the background — counts will update as it goes.`);
        // Poll the submissions endpoint every 8s for 3 minutes to live-update
        // the email_counts panel. Stops automatically once we leave the page.
        load();
        const start = Date.now();
        const poll = setInterval(async () => {
          if (Date.now() - start > 180_000) { clearInterval(poll); return; }
          try { await load(); } catch (_e) { /* ignore */ }
        }, 8_000);
      } else if (data.total_in_scope === 0) {
        toast.info(data.message || "Nothing to send.");
      } else {
        // Legacy synchronous shape (kept for safety / old deploys).
        const headline = `Resent to ${data.sent || 0}/${data.total_in_scope}` + (data.failed ? ` · ${data.failed} failed` : "");
        if (data.sent > 0) toast.success(headline);
        else toast.error(headline);
        load();
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkRunning(false); }
  };

  if (!info) return <div className="py-8 text-center text-[#7A828C]">Loading…</div>;

  const published = !!info.feedback_published;

  return (
    <div data-testid="feedback-admin" className="space-y-5">
      {/* Publish banner */}
      <div
        data-testid="feedback-publish-banner"
        className={`p-4 border rounded-sm flex items-center gap-4 flex-wrap ${published ? "border-[#A8D5BA] bg-[#EAF7EE]" : "border-[#E8D58A] bg-[#FFF8E5]"}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-mono-time text-[10px] uppercase tracking-widest ${published ? "text-[#1f6b3a]" : "text-[#7a5a00]"}`}>
              {published ? "Live to delegates" : "Hidden from delegates"}
            </span>
            <span className={`inline-block w-2 h-2 rounded-full ${published ? "bg-[#1f6b3a]" : "bg-[#b08a00]"}`} aria-hidden="true" />
          </div>
          <p className="text-sm text-[#1c1c24] leading-snug">
            {published
              ? "The Playlist tab in the bottom nav is replaced by a Feedback tab, and a CTA appears on Info."
              : "Feedback is hidden. Hit Publish when you're ready - the bottom nav swaps Playlist → Feedback automatically."}
          </p>
        </div>
        <Button
          onClick={() => setToggleDialog(true)}
          className={`rounded-sm ${published ? "bg-[#7A828C] text-[#FFFFFF] hover:bg-[#5a626c]" : "bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"}`}
          data-testid="feedback-publish-toggle"
        >
          {published ? <><EyeSlash size={14} className="mr-1" /> Hide</> : <><Eye size={14} className="mr-1" /> Publish feedback</>}
        </Button>
      </div>

      {/* Editable questions */}
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid="feedback-questions-panel">
        <div className="flex items-center gap-2 mb-1">
          <Question size={16} weight="duotone" className="text-[#000000]" />
          <h3 className="font-display text-base">Feedback questions</h3>
        </div>
        <p className="text-xs text-[#5a5a5a] mb-3">
          Rename the 5 questions delegates answer. The internal IDs (Q1-Q5) stay fixed, so previously-submitted feedback is preserved.
        </p>
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={q.key} data-testid={`feedback-question-row-${q.key}`}>
              <Label htmlFor={`fbq-${q.key}`} className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">
                Q{i + 1} <span className="text-[#1c1c24] normal-case font-sans tracking-normal">·</span> {q.key.toUpperCase()}
              </Label>
              <Input
                id={`fbq-${q.key}`}
                value={q.label}
                onChange={(e) => {
                  const v = e.target.value;
                  setQuestions((cur) => cur.map((c) => (c.key === q.key ? { ...c, label: v } : c)));
                }}
                className="h-11 rounded-sm mt-1"
                placeholder="Question text shown to delegates"
                data-testid={`feedback-question-input-${q.key}`}
                maxLength={250}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <Button
            onClick={saveQuestions}
            disabled={savingQuestions || questions.length === 0}
            className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
            data-testid="feedback-questions-save-btn"
          >
            <PencilSimple size={14} className="mr-1" />
            {savingQuestions ? "Saving…" : "Save questions"}
          </Button>
        </div>
      </div>

      {/* Email content */}
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center gap-2 mb-1">
          <ChatTeardropDots size={16} weight="duotone" className="text-[#000000]" />
          <h3 className="font-display text-base">Thank-you email</h3>
        </div>
        <p className="text-xs text-[#5a5a5a] mb-3">
          Sent automatically to every delegate who submits feedback. Plain text becomes paragraphs; basic HTML works too. The DFTB26 header + footer are added for you.
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="fb-subj">Subject line</Label>
            <Input
              id="fb-subj"
              value={info.feedback_email_subject || ""}
              onChange={(e) => setInfo((cur) => ({ ...cur, feedback_email_subject: e.target.value }))}
              placeholder="Thanks for your DFTB26 feedback"
              className="h-11 rounded-sm mt-1"
              data-testid="feedback-email-subject-input"
            />
          </div>
          <div>
            <Label htmlFor="fb-body">Body</Label>
            <Textarea
              id="fb-body"
              value={info.feedback_email_body || ""}
              onChange={(e) => setInfo((cur) => ({ ...cur, feedback_email_body: e.target.value }))}
              rows={10}
              placeholder={"Thanks for sharing your feedback - here are the DFTB26 quiz answers as promised:\n\n1. Answer one\n2. Answer two\n…"}
              className="rounded-sm mt-1 font-mono-time text-sm"
              data-testid="feedback-email-body-input"
            />
          </div>
          <div className="flex justify-end gap-2 flex-wrap">
            <Button
              onClick={sendPreviewEmail}
              disabled={sendingPreview}
              variant="outline"
              className="rounded-sm"
              data-testid="feedback-email-preview-btn"
            >
              <PaperPlaneTilt size={14} className="mr-1" />
              {sendingPreview ? "Sending…" : "Send test email to me"}
            </Button>
            <Button onClick={saveEmail} disabled={savingEmail} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="feedback-email-save-btn">
              {savingEmail ? "Saving…" : "Save email content"}
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-[#7A828C] mt-3 leading-relaxed">
          Sender: <strong className="text-[#1c1c24] font-mono-time">DFTB26 &lt;hello@dftbdigital.com&gt;</strong>. The test email goes to your admin email and is labelled <span className="font-mono-time">[PREVIEW]</span>.
        </p>
      </div>

      {/* Attachment */}
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid="feedback-attachment-panel">
        <div className="flex items-center gap-2 mb-1">
          <Paperclip size={16} weight="duotone" className="text-[#000000]" />
          <h3 className="font-display text-base">Email attachment</h3>
        </div>
        <p className="text-xs text-[#5a5a5a] mb-3">
          Upload one file (e.g. PDF of the quiz answers) - it will be attached to every thank-you email automatically. Max 20 MB.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          onChange={onFileChosen}
          className="hidden"
          data-testid="feedback-attachment-input"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.txt,.csv,.zip"
        />
        {info.feedback_attachment ? (
          <div className="flex items-center gap-3 p-3 border border-[#E5E9F0] rounded-sm bg-[#F5F7FA]" data-testid="feedback-attachment-current">
            <div className="w-10 h-10 grid place-items-center rounded-sm bg-[#000000] flex-shrink-0">
              <Paperclip size={18} weight="fill" className="text-[#43C4E3]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{info.feedback_attachment.name}</div>
              <div className="text-xs text-[#7A828C]">
                {formatBytes(info.feedback_attachment.size || 0)} · uploaded {info.feedback_attachment.uploaded_at ? new Date(info.feedback_attachment.uploaded_at).toLocaleString("en-GB") : ""}
              </div>
            </div>
            <Button
              onClick={onPickFile}
              variant="outline"
              size="sm"
              className="rounded-sm flex-shrink-0"
              disabled={uploading}
              data-testid="feedback-attachment-replace-btn"
            >
              Replace
            </Button>
            <Button
              onClick={deleteAttachment}
              variant="outline"
              size="sm"
              className="rounded-sm flex-shrink-0 text-[#a83232] hover:bg-[#a83232]/5 hover:text-[#a83232] border-[#a83232]/40"
              data-testid="feedback-attachment-delete-btn"
            >
              <Trash size={14} />
            </Button>
          </div>
        ) : (
          <Button
            onClick={onPickFile}
            disabled={uploading}
            className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
            data-testid="feedback-attachment-upload-btn"
          >
            <Paperclip size={14} className="mr-1" />
            {uploading ? "Uploading…" : "Upload attachment"}
          </Button>
        )}
      </div>

      {/* Submissions */}
      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="font-display text-base flex items-center gap-2">
            <ChatTeardropDots size={18} weight="duotone" className="text-[#000000]" />
            Submissions
            <span className="font-mono-time text-xs text-[#7A828C] font-normal">({submissions.length})</span>
          </h3>
          <div className="flex items-center gap-2">
            <Button
              onClick={generateSummary}
              disabled={summarising || submissions.length === 0}
              size="sm"
              className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
              data-testid="feedback-summary-btn"
            >
              {summarising ? <ArrowsClockwise size={14} weight="bold" className="mr-1 animate-spin" /> : <Sparkle size={14} weight="fill" className="mr-1" />}
              {summarising ? "Summarising…" : (summaryCache ? "Refresh AI summary" : "Generate AI summary")}
            </Button>
            <Button
              onClick={downloadCsv}
              variant="outline"
              size="sm"
              className="rounded-sm"
              disabled={submissions.length === 0}
              data-testid="feedback-csv-btn"
            >
              <DownloadSimple size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Email delivery dashboard — counts + bulk resend */}
        {submissions.length > 0 && (
          <div
            className="mb-4 p-3 border border-[#E5E9F0] rounded-sm bg-[#F5F7FA]"
            data-testid="feedback-email-delivery-panel"
          >
            <div className="flex items-center gap-2 mb-2">
              <PaperPlaneTilt size={16} weight="duotone" className="text-[#000000]" />
              <h4 className="font-display text-sm">Quiz answers email delivery</h4>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <DeliveryStat label="Sent" value={emailCounts.sent} tone="ok" testid="feedback-email-stat-sent" />
              <DeliveryStat label="Not yet sent" value={emailCounts.unknown} tone="warn" testid="feedback-email-stat-unknown" />
              <DeliveryStat label="Failed" value={emailCounts.failed} tone="err" testid="feedback-email-stat-failed" />
              <DeliveryStat label="No email" value={emailCounts.no_email} tone="muted" testid="feedback-email-stat-noemail" />
            </div>
            <div className="text-[11px] text-[#5a5a5a] leading-relaxed mb-3">
              Resend free plan caps at <strong>100 emails / day</strong>. If counts above show
              delegates without delivery, hit <em>Bulk resend</em> below (defaults to 90 per
              batch). Submissions made before this delivery tracking was added will appear as
              <em> Not yet sent</em> — you can safely resend them; delegates handle duplicates fine.
            </div>
            <Button
              onClick={() => { setBulkResult(null); setBulkDialog(true); }}
              disabled={bulkRunning || (emailCounts.unknown + emailCounts.failed === 0)}
              size="sm"
              className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
              data-testid="feedback-bulk-resend-btn"
            >
              <PaperPlaneTilt size={14} className="mr-1" />
              {bulkRunning ? "Sending…" : `Bulk resend (${emailCounts.unknown + emailCounts.failed} pending)`}
            </Button>
          </div>
        )}

        {summaryCache?.summary && (
          <SummaryPanel cache={summaryCache} />
        )}

        {submissions.length === 0 ? (
          <p className="text-sm text-[#7A828C] py-6 text-center">No feedback yet.</p>
        ) : (
          <div className="space-y-3">
            {submissions.map((s) => (
              <div key={s.id} className="p-3 border border-[#E5E9F0] rounded-sm" data-testid={`feedback-submission-${s.id}`}>
                <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{s.user_name || "(anon)"}</div>
                    <div className="text-xs text-[#7A828C] truncate">{s.user_email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <EmailStatusBadge sub={s} />
                    <Button
                      onClick={() => resendOne(s)}
                      disabled={!s.user_email || resendingOne === s.id || bulkRunning}
                      variant="outline"
                      size="sm"
                      className="rounded-sm h-7 px-2 text-xs"
                      data-testid={`feedback-resend-${s.id}`}
                      title={!s.user_email ? "No email on this submission" : "Resend quiz answers email"}
                    >
                      {resendingOne === s.id
                        ? <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                        : <PaperPlaneTilt size={12} weight="fill" />}
                      <span className="ml-1">{s.thank_you_email_status === "sent" ? "Resend" : "Send"}</span>
                    </Button>
                    <div className="text-[10px] text-[#7A828C] font-mono-time">
                      {new Date(s.created_at).toLocaleString("en-GB")}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    ["What made you come to DFTB26?", s.q1],
                    ["What can we do better?", s.q2],
                    ["What surprised you / will change your practice?", s.q3],
                    ["What else?", s.q4],
                    ["What was the best bit?", s.q5],
                  ].map(([label, text], i) => (
                    text ? (
                      <div key={i} className="text-sm">
                        <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mb-0.5">{label}</div>
                        <div className="text-[#1c1c24] whitespace-pre-wrap leading-relaxed">{text}</div>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toggle confirmation */}
      <AlertDialog open={toggleDialog} onOpenChange={setToggleDialog}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E5E9F0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {published ? "Hide feedback from delegates?" : "Publish feedback to delegates?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#5a5a5a] leading-relaxed">
              {published
                ? "The Feedback tab vanishes from the bottom nav and the Playlist tab returns. Existing submissions are kept."
                : "The bottom-nav Playlist tab will be replaced by a Feedback tab, and a CTA will appear on Info. Make sure your thank-you email content is ready first."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={togglePublished}
              className={`rounded-sm ${published ? "bg-[#a83232] hover:bg-[#8a2929]" : "bg-[#000000] hover:bg-[#1c1c24]"} text-[#FFFFFF]`}
              data-testid="feedback-publish-confirm"
            >
              {published ? "Hide feedback" : "Publish now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk resend confirmation */}
      <AlertDialog open={bulkDialog} onOpenChange={(o) => { if (!bulkRunning) setBulkDialog(o); }}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E5E9F0]" data-testid="feedback-bulk-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Bulk resend quiz answers</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#5a5a5a] leading-relaxed">
              We&#39;ll re-send the current thank-you email (with the uploaded attachment) to
              delegates matching your filter. Resend&#39;s free plan caps at 100 emails / day —
              keep batches under 100 to be safe.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-mono-time uppercase tracking-widest text-[#7A828C]">Who to send to</Label>
              <div className="mt-1 space-y-1">
                {[
                  ["unsent", `Not yet sent (${emailCounts.unknown + emailCounts.failed})`, "Recommended. Includes failed + delegates we have no record of sending to (e.g. before delivery tracking was added)."],
                  ["failed", `Only previously-failed (${emailCounts.failed})`, "Only retry sends that explicitly errored."],
                  ["all", `Everyone (${submissions.length - emailCounts.no_email})`, "Force a re-send to every delegate. Delegates may receive a duplicate."],
                ].map(([key, label, hint]) => (
                  <label
                    key={key}
                    className={`flex items-start gap-2 p-2 border rounded-sm cursor-pointer ${bulkTarget === key ? "border-[#000000] bg-[#F5F7FA]" : "border-[#E5E9F0]"}`}
                    data-testid={`feedback-bulk-target-${key}`}
                  >
                    <input
                      type="radio"
                      name="bulk-target"
                      value={key}
                      checked={bulkTarget === key}
                      onChange={() => setBulkTarget(key)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#1c1c24]">{label}</div>
                      <div className="text-[11px] text-[#7A828C] leading-relaxed">{hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="bulk-limit" className="text-xs font-mono-time uppercase tracking-widest text-[#7A828C]">Max emails this batch</Label>
              <Input
                id="bulk-limit"
                type="number"
                min={1}
                max={500}
                value={bulkLimit}
                onChange={(e) => setBulkLimit(e.target.value)}
                className="h-10 rounded-sm mt-1 w-32 font-mono-time"
                data-testid="feedback-bulk-limit-input"
              />
              <div className="text-[11px] text-[#7A828C] mt-1">Default 90 (leaves headroom under Resend&#39;s 100/day cap).</div>
            </div>
            {bulkResult && (
              <div className="text-xs p-2 bg-[#F5F7FA] border border-[#E5E9F0] rounded-sm font-mono-time" data-testid="feedback-bulk-result">
                {bulkResult.background ? (
                  <>
                    <div>Queued: <strong>{bulkResult.queued}</strong> of {bulkResult.total_in_scope} (running in background)</div>
                    <div className="text-[#5a5a5a] mt-1">{bulkResult.message}</div>
                  </>
                ) : (
                  <>
                    <div>Sent: <strong>{bulkResult.sent || 0}</strong> · Failed: <strong>{bulkResult.failed || 0}</strong> · Skipped: <strong>{bulkResult.skipped || 0}</strong></div>
                    <div>Remaining in scope after this batch: <strong>{bulkResult.remaining_in_scope ?? bulkResult.total_in_scope}</strong></div>
                    {bulkResult.last_error && <div className="text-[#a83232] mt-1">Last error: {bulkResult.last_error}</div>}
                  </>
                )}
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" disabled={bulkRunning}>Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runBulkResend(); }}
              disabled={bulkRunning}
              className="rounded-sm bg-[#000000] hover:bg-[#1c1c24] text-[#FFFFFF]"
              data-testid="feedback-bulk-resend-confirm"
            >
              {bulkRunning
                ? <><CircleNotch size={14} className="mr-1 animate-spin" /> Sending…</>
                : <><PaperPlaneTilt size={14} className="mr-1" /> Send now</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DeliveryStat({ label, value, tone, testid }) {
  const toneMap = {
    ok: "bg-[#EAF7EE] border-[#A8D5BA] text-[#1f6b3a]",
    warn: "bg-[#FFF8E5] border-[#E8D58A] text-[#7a5a00]",
    err: "bg-[#FDECEC] border-[#E8A8A8] text-[#a83232]",
    muted: "bg-[#FFFFFF] border-[#E5E9F0] text-[#5a5a5a]",
  };
  return (
    <div className={`p-2 border rounded-sm ${toneMap[tone] || toneMap.muted}`} data-testid={testid}>
      <div className="font-mono-time text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="font-display text-xl leading-none mt-0.5">{value}</div>
    </div>
  );
}

function EmailStatusBadge({ sub }) {
  if (!sub.user_email) {
    return (
      <span className="font-mono-time text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#FFFFFF] border border-[#E5E9F0] text-[#7A828C]" data-testid={`feedback-status-${sub.id}-noemail`}>
        no email
      </span>
    );
  }
  const status = sub.thank_you_email_status;
  if (status === "sent") {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono-time text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#EAF7EE] border border-[#A8D5BA] text-[#1f6b3a]"
        title={sub.thank_you_email_sent_at ? `Sent ${new Date(sub.thank_you_email_sent_at).toLocaleString("en-GB")}` : ""}
        data-testid={`feedback-status-${sub.id}-sent`}
      >
        <CheckCircle size={10} weight="fill" /> sent
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono-time text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#FDECEC] border border-[#E8A8A8] text-[#a83232]"
        title={sub.thank_you_email_last_error || "Send failed"}
        data-testid={`feedback-status-${sub.id}-failed`}
      >
        <WarningCircle size={10} weight="fill" /> failed
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 font-mono-time text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#FFF8E5] border border-[#E8D58A] text-[#7a5a00]"
      title="No delivery record - submission may pre-date tracking, or the email hasn't been attempted yet."
      data-testid={`feedback-status-${sub.id}-unknown`}
    >
      <WarningCircle size={10} weight="fill" /> not sent
    </span>
  );
}

function SummaryPanel({ cache }) {
  const s = cache.summary || {};
  const sentiment = s.overall_sentiment || {};
  return (
    <div data-testid="feedback-summary-panel" className="mb-5 p-4 bg-[#000000] text-[#FFFFFF] rounded-sm space-y-4">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#43C4E3]">AI summary · {cache.submission_count} responses</div>
        <div className="font-mono-time text-[10px] text-[#7A828C]">
          {cache.generated_at ? new Date(cache.generated_at).toLocaleString("en-GB") : ""}
        </div>
      </div>

      {s.headline && (
        <div className="font-display text-lg sm:text-xl leading-snug" data-testid="feedback-summary-headline">
          &ldquo;{s.headline}&rdquo;
        </div>
      )}

      {sentiment.label && (
        <div className="text-sm">
          <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#43C4E3] mr-2">Overall</span>
          <span className="font-semibold capitalize">{sentiment.label}</span>
          {typeof sentiment.score === "number" && <span className="text-[#7A828C]"> · {sentiment.score.toFixed(2)}</span>}
          {sentiment.summary && <div className="text-[#E5E9F0] mt-1 leading-relaxed">{sentiment.summary}</div>}
        </div>
      )}

      {(s.per_question || []).length > 0 && (
        <div>
          <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#43C4E3] mb-2">Themes by question</div>
          <div className="space-y-3">
            {s.per_question.map((q, i) => (
              <div key={i} className="bg-[#FFFFFF]/5 rounded-sm p-3" data-testid={`feedback-summary-q-${q.question_index}`}>
                <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mb-1">Q{q.question_index} · {q.question_label}</div>
                <ul className="space-y-1.5 text-sm">
                  {(q.themes || []).map((t, j) => (
                    <li key={j} className="leading-relaxed">
                      <span className="font-semibold">{t.theme}</span>
                      {typeof t.count_estimate === "number" && <span className="text-[#7A828C]"> · ~{t.count_estimate} mentions</span>}
                      {t.description && <span className="text-[#E5E9F0]"> — {t.description}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {(s.standout_quotes || []).length > 0 && (
        <div>
          <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#43C4E3] mb-2">Standout quotes</div>
          <div className="space-y-2">
            {s.standout_quotes.map((q, i) => (
              <div key={i} className="text-sm border-l-2 border-[#43C4E3] pl-3" data-testid={`feedback-summary-quote-${i}`}>
                <div className="italic text-[#FFFFFF]">&ldquo;{q.quote}&rdquo;</div>
                <div className="text-[10px] text-[#7A828C] font-mono-time mt-0.5">
                  {q.delegate || "Anonymous"}{q.why ? ` — ${q.why}` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(s.what_to_improve_next_year || []).length > 0 && (
        <div>
          <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#43C4E3] mb-2">What to improve next year</div>
          <ul className="list-disc pl-5 text-sm space-y-1 text-[#E5E9F0]">
            {s.what_to_improve_next_year.map((item, i) => (
              <li key={i} data-testid={`feedback-summary-improve-${i}`}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
